import React from 'react'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { Alert, Progress } from 'reactstrap'
import { FormattedMessage } from 'react-intl'
import { AlertCircle, Check } from 'react-feather'

import CheckBox from '~/components/Checkbox'
import { Separator, Show } from '~/components/commons'
import { queries } from '~/constants/mediaQuery'
import LocationNamesFilter from '~/components/LocationsFilters/LocationNamesFilter'
import { CLIENT_ADMIN } from '~/constants/roles'

function FilterPanel({ initialValue }) {
  const canImportProjects = useSelector(
    state => state.user?.profile?.account?.importProject
  )
  const userProfile = useSelector(state => state.user?.profile)
  const isClientAdmin = userProfile?.role === CLIENT_ADMIN

  const { setFieldValue, values, errors } = useFormikContext()

  const [updateAllLocations, setUpdateAllLocations] = React.useState(false)
  const [updateAllProjects, setUpdateAllProjects] = React.useState(false)
  const locationsFilterRef = React.useRef()

  function handleChangeUpdateAll(event) {
    if (!locationsFilterRef.current) return

    const { checked } = event.target

    if (checked) {
      locationsFilterRef.current.clear()
      setUpdateAllProjects(false)
    }

    setFieldValue(
      'locationNames',
      checked ? locationsFilterRef.current.allNames : []
    )

    setUpdateAllLocations(checked)
  }

  function handleChangeAllProjects(event) {
    if (!locationsFilterRef.current) return

    const { checked } = event.target

    if (checked) {
      locationsFilterRef.current.clear()
      setUpdateAllLocations(false)
    }

    setFieldValue(
      'locationNames',
      checked ? locationsFilterRef.current.projects : []
    )

    setUpdateAllProjects(checked)
  }

  const numSelectedLocations = React.useMemo(() => {
    return updateAllLocations
      ? locationsFilterRef.current?.total
      : updateAllProjects
      ? Array.from(new Set(locationsFilterRef.current?.projects || [])).length
      : Array.from(new Set(values?.locationNames || [])).length
  }, [updateAllLocations, updateAllProjects, values])

  return (
    <Filters>
      <div className="font-medium-1 text-bold-500" htmlFor="schedule-date">
        <FormattedMessage id="Create in" />:
      </div>

      <div className="d-flex">
        <div style={{ flex: 1 }}>
          <Progress
            className="progress-xl mr-1"
            value={
              (numSelectedLocations /
                (locationsFilterRef.current?.total ?? 1)) *
              100
            }
            color={updateAllProjects ? 'tertiary' : 'primary'}
          />
        </div>
        <span>
          {numSelectedLocations ?? 0} <FormattedMessage id="Locations" />
        </span>
      </div>

      <div className="d-flex justify-content-between">
        <CheckBox
          color="primary"
          label="Select all locations from this project"
          size="md"
          icon={<Check color="#fff" size={15} />}
          checked={updateAllLocations ?? false}
          onChange={handleChangeUpdateAll}
        />

        {canImportProjects && isClientAdmin && (
          <CheckBox
            color="tertiary"
            label="Select all locations from all projects"
            size="md"
            icon={<Check color="#fff" size={15} />}
            checked={updateAllProjects ?? false}
            onChange={handleChangeAllProjects}
          />
        )}
      </div>

      <PanelContainer hide={!!updateAllLocations && !!updateAllProjects}>
        <Separator className="mb-1" uppercase>
          <FormattedMessage id="Or filter by" />
        </Separator>

        <LocationNamesFilter
          initialValue={initialValue}
          ref={locationsFilterRef}
          onChange={locationNames => {
            if (!updateAllLocations && !updateAllProjects) {
              setFieldValue('locationNames', locationNames)
            }
          }}
          disabled={updateAllLocations || updateAllProjects}
        />
      </PanelContainer>
      <Show condition={!!errors?.locationNames}>
        <Alert className="m-0" color="danger" isOpen={!!errors?.locationNames}>
          <AlertCircle size={15} />{' '}
          <span>
            <FormattedMessage id={errors?.locationNames} />
          </span>
        </Alert>
      </Show>
    </Filters>
  )
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media ${queries.less.sm} {
    display: ${props => (props.hide ? 'none' : 'flex')};
  }
`

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export default FilterPanel
